/* React Higher Order Component which fetches short course data for channel where channelId = props.channelId
    Child (InnerComponent) will only render once short course data is successfully fetched.
    child will receive props: {
        channelId: PropTypes.number,
        fatalError: PropTypes.object,
        shortCourseStoreState: PropTypes.shape({ initialized: PropTypes.bool, shortCourses: PropTypes.object })
    }

*/

import React from 'react';
import PropTypes from 'prop-types';
const uuid = require('uuid');

const ShortCourseActions = require('../../actions/ShortCourseActions');
const ShortCourseStore = require('../../stores/ShortCourseStore');
import LoadingMainContent from '../loading/LoadingMainContent';
import ErrorBox from '../common/ErrorBox';
const ReactUtil = require('../../util/ReactUtil');

const propTypes = {
    channelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    forceRefreshOnMount: PropTypes.bool, // force refresh of activity data, even if activity store already has data for the given id
    hideLoadingIcon: PropTypes.bool
};

const defaultProps = {
    forceRefreshOnMount: false,
    hideLoadingIcon: false
};

export default function withChannelShortCourses(WrappedComponent) {
    class WithChannelShortCourses extends React.Component {
        constructor(props) {
            super(props);

            this.state = {
                shortCourseStoreState: ShortCourseStore.getStateChannel(props.channelId),
                getActionId: null,
                fatalError: null
            }

            this._getScs = this._getScs.bind(this);
            this.unsubscribes = [ShortCourseStore.listen(this.onShortCourseStoreChange.bind(this))];
        }

        componentDidMount() {
            if (!this.state.shortCourseStoreState.initialized || this.props.forceRefreshOnMount) {
                this._getScs(this.props.channelId);
            }
        }

        componentDidUpdate(prevProps) {
            if (this.props.channelId !== prevProps.channelId) {
                const shortCourseStoreState = ShortCourseStore.getStateChannel(this.props.channelId);
                if (!shortCourseStoreState.initialized) {
                    this._getScs(this.props.channelId);
                }

                this.setState({ shortCourseStoreState: shortCourseStoreState });
            }
        }

        componentWillUnmount() {
            this.unsubscribes.forEach(u => u());
        }

        _getScs(channelId) {
            const actionId = uuid.v4();
            this.setState({ getActionId: actionId }, () => {
                ShortCourseActions.getChannelShortCourses(actionId, { channelId: channelId });
            });
        }

        onShortCourseStoreChange(newState) {
            let additionalState = {};
            if (newState.action.id === this.state.getActionId) {
                additionalState = { ...additionalState, getActionId: null };
                if (!!newState.action.error) {
                    additionalState = { ...additionalState, fatalError: newState.action.error };
                }

            }

            this.setState({
                shortCourseStoreState: ShortCourseStore.getStateChannel(this.props.channelId),
                ...additionalState
            });
        }

        render() {
            if (this.state.fatalError) {
                return <ErrorBox error={this.state.fatalError} />;
            }

            if (!this.state.shortCourseStoreState.initialized) {
                if (this.props.hideLoadingIcon) {
                    return null;
                }
                else {
                    return <LoadingMainContent/>;
                }
            }

            return (
                <WrappedComponent
                    {...this.props}
                    shortCourseStoreState={this.state.shortCourseStoreState}
                />
            );
        }
    }

    WithChannelShortCourses.propTypes = propTypes;
    WithChannelShortCourses.defaultProps = defaultProps;
    WithChannelShortCourses.displayName = `WithChannelShortCourses(${ReactUtil.getComponentDisplayName(WrappedComponent)})`
    return WithChannelShortCourses;
}
